import { API_CONFIG } from '../config/api.config';
import { LoginResponse } from '../types';

export interface LoginCredentials {
  email: string;
  password: string;
}

class AuthService {
  private authToken: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.authToken = localStorage.getItem('authToken');
  }

  /**
   * Login user with credentials
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await fetch(`${API_CONFIG.LOGIN_URL}${API_CONFIG.ENDPOINTS.LOGIN}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    const contentType = response.headers.get('content-type');
    const data = (contentType && contentType.includes('application/json')) 
      ? await response.json().catch(() => ({}))
      : {};

    if (!response.ok) {
      throw new Error(data.detail || data.message || 'Login failed');
    }

    // Extract JWT token from response body
    const token = data.access_token;
    if (!token) {
      throw new Error('No access token received');
    }

    // Store token
    this.setToken(token);

    return data;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      // Call logout endpoint if token exists
      if (this.authToken) {
        await fetch(`${API_CONFIG.LOGIN_URL}${API_CONFIG.ENDPOINTS.LOGOUT}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.authToken}`,
          },
          body: JSON.stringify({}),
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear token regardless of API call success
      this.clearToken();
    }
  }

  /**
   * Get current auth token
   */
  getToken(): string | null {
    return this.authToken || localStorage.getItem('authToken');
  }

  /**
   * Set auth token
   */
  setToken(token: string): void {
    this.authToken = token;
    localStorage.setItem('authToken', token);
  }

  /**
   * Clear auth token
   */
  clearToken(): void {
    this.authToken = null;
    localStorage.removeItem('authToken');
  }

  /**
   * Get user ID from JWT token
   */
  getUserId(): number | null {
    const token = this.getToken();
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub ? parseInt(payload.sub) : null;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Get authorization headers
   */
  getAuthHeaders(): HeadersInit {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  /**
   * Refresh token (if endpoint becomes available)
   */
  async refreshToken(): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No token to refresh');
    }

    const response = await fetch(`${API_CONFIG.LOGIN_URL}/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ refresh_token: token }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const authHeader = response.headers.get('Authorization');
    if (authHeader) {
      const newToken = authHeader.startsWith('Bearer ') 
        ? authHeader.substring(7) 
        : authHeader;
      this.setToken(newToken);
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
